/**
 * Factory de métodos CRUD estándar — genera getAll, getById, create, update, delete y lista para un endpoint REST
 */

/**
 * Genera métodos CRUD estándar para un endpoint REST
 *
 * Soporta omitir métodos individuales via config.methods y renombrarlos via config.methodNames.
 * La función es equivalente al método privado #createCrudMethods de la clase ApiService original,
 * pero recibe authenticatedRequest como dependencia inyectada en lugar de usar this.
 *
 * @param {Object} config - Configuración del endpoint
 * @param {string} config.endpoint - Ruta base del endpoint (ej: '/api/equipos')
 * @param {string} config.entityName - Nombre de la entidad en singular (ej: 'equipo')
 * @param {string} [config.entityNamePlural] - Nombre en plural (default: entityName + 's')
 * @param {Object} [config.methods] - Flags para omitir o agregar métodos
 * @param {boolean} [config.methods.getAll=true] - Generar GET /endpoint
 * @param {boolean} [config.methods.getById=true] - Generar GET /endpoint/:id
 * @param {boolean} [config.methods.create=true] - Generar POST /endpoint
 * @param {boolean} [config.methods.update=true] - Generar PUT /endpoint/:id
 * @param {boolean} [config.methods.delete=true] - Generar DELETE /endpoint/:id
 * @param {boolean} [config.methods.lista=false] - Generar GET /endpoint/lista
 * @param {Object} [config.methodNames] - Nombres personalizados para métodos generados
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @returns {Object} Métodos CRUD generados
 */
export function createCrudMethods(config, { authenticatedRequest }) {
  const {
    endpoint,
    entityName,
    entityNamePlural = entityName + 's',
    methods = {},
    methodNames = {}
  } = config;

  /**
   * Capitaliza la primera letra de un string
   * @param {string} s - String a capitalizar
   * @returns {string} String con primera letra mayúscula
   */
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const Name = capitalize(entityName);
  const Names = capitalize(entityNamePlural);
  const result = {};

  // GET list con params
  if (methods.getAll !== false) {
    result[methodNames.getAll || `get${Names}`] = async (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return await authenticatedRequest(qs ? `${endpoint}?${qs}` : endpoint);
    };
  }

  // GET by ID
  if (methods.getById !== false) {
    result[methodNames.getById || `get${Name}ById`] = async (id) => {
      return await authenticatedRequest(`${endpoint}/${id}`);
    };
  }

  // CREATE
  if (methods.create !== false) {
    result[methodNames.create || `create${Name}`] = async (data) => {
      return await authenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    };
  }

  // UPDATE
  if (methods.update !== false) {
    result[methodNames.update || `update${Name}`] = async (id, data) => {
      return await authenticatedRequest(`${endpoint}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    };
  }

  // DELETE
  if (methods.delete !== false) {
    result[methodNames.delete || `delete${Name}`] = async (id) => {
      return await authenticatedRequest(`${endpoint}/${id}`, {
        method: 'DELETE'
      });
    };
  }

  // GET /lista
  if (methods.lista === true) {
    result[methodNames.lista || `get${Names}Lista`] = async () => {
      return await authenticatedRequest(`${endpoint}/lista`);
    };
  }

  return result;
}
